import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { MapPin, MessageSquareText, Clock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUSINESS, IMAGES, IMAGE_FALLBACK } from "@/data/content";
import { WhatsAppButton } from "./WhatsAppButton";
import { ImageWithFallback } from "./ImageWithFallback";

const Badge = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(201,169,110,0.28)] bg-[rgba(20,21,27,0.6)] px-3 py-1.5 text-xs text-foreground/85">
    <Icon className="h-3.5 w-3.5 text-[#C9A96E]" />
    {children}
  </span>
);

export function Hero() {
  const { t, lang } = useLanguage();
  return (
    <section id="top" className="relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 600px at 18% 8%, rgba(201,169,110,0.14), transparent 55%), radial-gradient(900px 500px at 82% 0%, rgba(216,192,138,0.10), transparent 60%)",
        }}
        aria-hidden="true"
      />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pt-20 lg:pt-24 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <p className="text-xs uppercase tracking-[0.18em] text-[#C9A96E] mb-4">
              {t.hero.overline}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-foreground">
              {t.hero.title}
            </h1>
            <p className="mt-5 text-sm md:text-base text-muted-foreground max-w-prose leading-relaxed">
              {t.hero.subtitle}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <WhatsAppButton source="hero" testId="hero-whatsapp-cta">
                {t.cta.book}
              </WhatsAppButton>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-[rgba(201,169,110,0.4)] text-foreground hover:bg-[rgba(201,169,110,0.10)]"
              >
                <a href="#services" data-testid="hero-services-scroll-cta">
                  {t.cta.viewServices}
                </a>
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap gap-2.5">
              <Badge icon={MapPin}>{t.hero.badge1}</Badge>
              <Badge icon={MessageSquareText}>{t.hero.badge2}</Badge>
              <Badge icon={Clock}>{t.hero.badge3}</Badge>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-[rgba(201,169,110,0.32)] shadow-[0_18px_60px_rgba(0,0,0,0.55)] bg-black">
              <div className="aspect-[4/5] w-full p-6 sm:p-10 flex items-center justify-center">
                <ImageWithFallback
                  src={IMAGES.logo}
                  fallback={IMAGE_FALLBACK}
                  priority
                  alt={
                    lang === "no"
                      ? "Logo \u2013 Beauty By Magdalena, skj\u00f8nnhetssalong i Oslo Sentrum"
                      : "Logo \u2013 Beauty By Magdalena, beauty salon in Oslo Sentrum"
                  }
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
            <div className="absolute -bottom-4 left-4 right-4 sm:left-6 sm:right-6 rounded-xl border border-[rgba(201,169,110,0.28)] bg-[rgba(11,11,15,0.92)] backdrop-blur-md px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-foreground/90">
                <MapPin className="h-4 w-4 text-[#C9A96E]" />
                <span>{BUSINESS.address}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
